import React, { useEffect, useState, useMemo, useCallback } from 'react';
import Select from 'react-select';
import useParamsGetter from "../../hooks/useParamsGetter";
import BackBtn from "../UI/BackBtn";
import { years, tableIcons } from "../../helpers";
import DownloadLink from "../common/DownloadLink";
import {
    getStickyColumns, localization, modifyDefaultOptions, objectToCols,
  } from '../../helpers/table';
import { getAllUnified, getUnifiedByEntityId } from "../../services/factsSvc";
import useUserContext from "../../hooks/useUserContext";
import MaterialTable, { MTableToolbar } from 'material-table';
import LoadingTable from '../table/LoadingTable';
import { dateRenderer } from '../../services/formatSvc';
import { isAdminRead } from "../../services/rolesSvc";
import { BaseRequests } from '../../requests/BaseRequests';
import useNavProject from '../../hooks/useNavProject';
import { getCols } from '../../helpers/unified';
import { getFieldRenderFunction, indexValueRenderer, renderEditMoneyComponent } from '../../pages/index/Shared';
import useAPI from '../../hooks/useAPI';

const DEFAULT_YEAR = Number(DEFAULT_YEAR_WEBPACK);
const months = ['04', '07', '10'];

const BaseDetailUnifiedPcsByStratProj = () => {
    const api = useAPI();
    const userCtx = useUserContext();
    const navProject = useNavProject();

    const [data, setData]= useState([]);
    const [portfolioList, setPortfolioList]= useState([]);
    const [indexName, setIndexName]= useState('');
    const [filterYears, setFilterYears] = useState([DEFAULT_YEAR]);
    const [filterPortfolio, setFilterPortfolio] = useState(null);
    const [isLoaded, setIsLoaded] = useState(true);
    const [entityId, stratId, stratName, hasPortfolio] = useParamsGetter('entityId', 'stratId', 'stratName', 'hasPortfolio');

    const canExport = useMemo(() => userCtx.perms.includes('kpiAdmin') || isAdminRead(userCtx), []);
    const colsLeft = useMemo(() => getCols(data, { stratId, stratLevel: true }, null, navProject) || [], [navProject, data]);

    const portfolioOptions = useMemo(() => portfolioList.map((portf) => {
        return {
            label: portf.name,
            value: portf.originId,
        }
    }), [portfolioList])

    const handleChangeYears = (event) => {
        const yearValue = parseInt(event.target.value);
        const newFilterYears = filterYears.includes(yearValue)
          ? filterYears.filter((item) => item !== yearValue).sort()
          : [...filterYears, yearValue].sort();
        setFilterYears(newFilterYears);
    };

    const getColumns = () => {
        const byYearsColsFactory = (year) => {
            const result = [];
            months.forEach((m) => {
                result.push({
                    title: `Факт на 01.${m}.${year}`,
                    field: `fact${m}_${year}`,
                    render: (rowData) => indexValueRenderer(rowData.unit, rowData[`fact${m}_${year}`]),
                    filterCellStyle: { minWidth: '155px' },
                });
            });

            return result;
        };

        const colsMiddle = filterYears.reduce(
            (acc, year) => [
                ...acc,
                {
                    title: `План ${year}`,
                    field: `plan${year}`,
                    editComponent: renderEditMoneyComponent,
                    render: (rowData) => indexValueRenderer(rowData.unit, rowData[`plan${year}`]),
                    filterCellStyle: { minWidth: '155px' },
                },
                    ...byYearsColsFactory(year),
                {
                    title: `Факт ${year}`,
                    field: `fact${year}`,
                    render: (rowData) => indexValueRenderer(rowData.unit, rowData[`fact${year}`]),
                    filterCellStyle: { minWidth: '155px' },
                },
            ],
            [],
        );

        return [...getStickyColumns(colsLeft, 0), ...colsMiddle];
    };

    const getData = async () => {
        setIsLoaded(false);
        try {
            const result = await getUnifiedByEntityId(entityId, {
                years: filterYears,
                strategyId: stratId,
            });

            setData(result
                .filter((proj) => {
                    if (!filterPortfolio) return proj
                    return proj?.entityProperties?.portfId === filterPortfolio.value
                })
                .map((proj, i) => {
                    return ({
                        ...proj,
                        num: i + 1,
                        projHead: proj?.entityProperties?.head?.displayName,
                    })
                })
            );
        } catch (error) {
            console.error('Ошибка при запросе данных: ', error);
        } finally {
            setIsLoaded(true)
        }
    }

    const getIndexName = useCallback(async () => {
        try {
            const allIndexes = await getAllUnified({
                years: filterYears,
                strategyId: stratId,
            });

            const index = allIndexes?.find((i) => i.entityId === Number(entityId))
            setIndexName(index.name)
        } catch (error) {
            console.error(`Ошибка при загрузке названия показателя: ${error}`)
        }
    }, [])

    const getPortfoliosList = useCallback(async () => {
        try {
            const result = await api.get(`/portfolios?strategyId=${stratId}`);
            setPortfolioList(result)
        } catch (error) {
            console.error(`Ошибка при загрузке списка портфелей: ${error}`)
        }
    }, [])

    useEffect(() => {
        getData();
    }, [filterYears, filterPortfolio]);

    useEffect(() => {
        getIndexName();
        if (hasPortfolio) {
            getPortfoliosList()
        }
    }, []);

    return (
        <section style={{ padding: '2.5rem 3rem' }} className="page project-page">
            <BackBtn />
            <LoadingTable spinnerStyle={{ marginTop: '50px' }} loadingObject={{ isLoaded }}>
                <MaterialTable
                    icons={tableIcons}
                    localization={localization}
                    columns={objectToCols(getColumns())}
                    data={data}
                    options={modifyDefaultOptions({
                        columnsButton: true,
                        maxBodyHeight: 'calc(100vh - 442px)',
                        paging: false,
                        headerStyle: { height: 50, position: 'sticky' },
                        filterCellStyle: {
                            position: 'sticky',
                            top: 54,
                            background: 'white',
                        },
                    })}
                    components={Object.assign({
                        Toolbar: (props) => (
                            <div>
                                <h3 style={styledTitle}>
                                    {indexName}
                                    <br />
                                    {`СП${stratId} ${decodeURI(stratName)}`}
                                </h3>

                                <div className="filters-container" style={styledToolbar}>
                                    <div className="year-checkboxes">
                                        {years.map((year) => (
                                            <label key={year}>
                                                <input
                                                    type="checkbox"
                                                    value={year}
                                                    onChange={handleChangeYears}
                                                    checked={filterYears.includes(year)}
                                                />
                                                <span className="tag">{year}</span>
                                            </label>
                                        ))}
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        {hasPortfolio && (
                                            <div style={styledPortfolioSelect}>
                                                <Select
                                                    placeholder="Выбрать портфель"
                                                    name="portfolio"
                                                    options={portfolioOptions}
                                                    onChange={setFilterPortfolio}
                                                    value={filterPortfolio || null}
                                                    isSearchable={false}
                                                    isClearable={true}
                                                />
                                            </div>
                                        )}

                                        <div style={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                                            <MTableToolbar {...props} />
                                        </div>

                                        {canExport && (
                                            <div style={{ flex: 'none', marginLeft: 16 }}>
                                                <DownloadLink
                                                    variant="outline-primary"
                                                    url={BaseRequests.getUrlSearch(`/reports/fixedPcs/${entityId}/export`,{
                                                            years:JSON.stringify(filterYears),
                                                            strategyId: stratId,
                                                        })}
                                                    filename={`Отчет по показателю_${indexName}_СП${stratId}_${decodeURI(stratName)}_${dateRenderer(new Date())}.xlsx`}
                                                >
                                                    Выгрузить в Excel
                                                </DownloadLink>
                                            </div>
                                        )}
                                    </div>

                                </div>
                            </div>
                        ),
                    }, {})}
                    title=''
                />
            </LoadingTable>
        </section>
    );
}

export default BaseDetailUnifiedPcsByStratProj;

const styledTitle = {
    marginTop: '1.5em',
    fontSize: '20px',
    textAlign: 'start',
};

const styledToolbar = {
    alignItems: 'end',
    padding: '16px 0 35px 0',
};

const styledPortfolioSelect = {
    width: '350px',
    marginRight: '15px',
    zIndex: 99,
};
